// bootstrap5 behaviour-contract implementation.
//
// Implements all eight base hooks using Bootstrap 5 native widgets and
// CSS where possible, plus a small CSS-only blockUI overlay and a
// matchMedia breakpoint helper. Imports nothing — relies on the global
// `bootstrap` namespace shipped by the Bootstrap 5 JS bundle.
//
// Replaces base's stub via the theme's index.js:
//   hiveMind.behaviors = require('./scripts/behaviors');
//
// See core/themes/base/scripts/behaviors.contract.md for the
// per-hook spec. Notes on tier-specific divergences:
//
// * submenu — Bootstrap.Dropdown anchors the menu to its own toggle (the
//   toggle and menu must share a .dropdown parent). KTMenu's "anchor menu
//   to a remote element" semantic is not native here. show(targetEl) is
//   accepted but the menu opens against its own toggle.
// * drawer — uses bootstrap.Offcanvas. Contract's destroy() maps to
//   Offcanvas.dispose().
// * blockUI — pure CSS overlay using bootstrap utility classes plus a
//   bootstrap5 .zit-blockui-* helper class. No JS dependency.
// * breakpoint — matchMedia against Bootstrap's documented px breakpoints.
// * bridgeTierAttrs — mirrors zit-namespaced markers onto data-bs-*
//   equivalents so Bootstrap's auto-discovery (collapse trigger document
//   click handler, Tooltip auto-init scan, Toast close button) finds the
//   markup. Called document-wide once at boot from app.initTheme, and per-
//   subtree by base consumers that insert dynamic markup.

// helper.js carries mirrorDataAttribute used by bridgeTierAttrs. Required
// directly so this module is self-contained at module-load time.
const helper = require('../../base/scripts/helper');

// Bootstrap 5.3 breakpoint min-widths (px). Same scale Metronic 8 uses.
const BS_BREAKPOINTS = {
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1400
};

// Bootstrap's literal class tokens for collapse runtime state. Lifted to
// module scope so collapse.stateClass() is a single map lookup.
const BS_COLLAPSE_STATE_CLASSES = {
    root: 'collapse',
    shown: 'show',
    hidden: 'hide'
};

// Bootstrap's literal class tokens for activation state of UI controls
// (active page in pagination, picked option in a menu, disabled control).
const BS_ACTIVATION_STATE_CLASSES = {
    active: 'active',
    disabled: 'disabled'
};

// Map base's neutral lifecycle event names ('shown' / 'hidden') to the
// underlying Bootstrap event names emitted by each widget.
const BS_DROPDOWN_EVENTS = {
    shown: 'shown.bs.dropdown',
    hidden: 'hidden.bs.dropdown'
};
const BS_COLLAPSE_EVENTS = {
    shown: 'shown.bs.collapse',
    hidden: 'hidden.bs.collapse'
};
const BS_POPOVER_EVENTS = {
    shown: 'shown.bs.popover',
    hidden: 'hidden.bs.popover'
};

const behaviors = {
    // Modal: Bootstrap.Modal exposes show/hide/dispose directly. Returning
    // the instance satisfies the contract via duck typing.
    modal: {
        create: function (el) {
            // Relay Bootstrap's framework-namespaced dismiss event to the neutral
            // 'zitModalHidden' the contract defines (see modal.create in
            // behaviors.contract.md). base's confirm/session consumers listen only
            // for the neutral name, so they stay framework-agnostic; cleanSlate
            // dispatches 'zitModalHidden' directly from its own adapter. Bootstrap
            // fires 'hidden.bs.modal' only on a real hide (not on dispose), which is
            // exactly the neutral event's contract.
            el.addEventListener('hidden.bs.modal', function () {
                el.dispatchEvent(new Event('zitModalHidden', { bubbles: true }));
            });
            return new bootstrap.Modal(el);
        }
    },

    // Collapse: wraps Bootstrap.Collapse so we can expose neutral event
    // names via on() in addition to the show/hide/dispose passthrough.
    collapse: {
        // Resolve a runtime collapse-state class name to Bootstrap's token.
        // 'root' -> 'collapse', 'shown' -> 'show', 'hidden' -> 'hide'.
        // Unknown names throw so typos surface immediately rather than
        // silently producing the empty string at the call site.
        stateClass: function (name) {
            const tokenString = BS_COLLAPSE_STATE_CLASSES[name];
            if (typeof tokenString === 'undefined') {
                throw new Error('zit collapse.stateClass: unknown state "' + name + '"');
            }
            return tokenString;
        },

        // Apply initial collapse-state classes to [data-zit-collapsible]
        // regions inside rootEl. Triggers ([data-zit-toggle="collapse"]) are
        // not wired here — Bootstrap's document-level click handler picks
        // them up automatically after bridgeTierAttrs has mirrored the
        // zit-namespaced attribute to data-bs-toggle (boot: app.initTheme;
        // per-subtree: consumers after dynamic insert). This hook handles
        // only the body side: applying 'collapse' (root) + 'show' or 'hide'
        // (initial state) so Bootstrap.Collapse's plugin can take over from
        // the correct visual state when the first user toggle fires.
        //
        // Scope is inclusive — rootEl itself is processed if it carries the
        // marker, then descendants. Diverges from bridgeTierAttrs (descendants-
        // only) because dynamic-insertion callers typically pass the newly-
        // inserted root that IS the collapsible body wrapper.
        //
        // Idempotent: elements already processed carry data-zit-collapse-
        // initialized="true" and are skipped on re-scan.
        createInstances: function (rootEl) {
            // Default to document for the boot-time bulk scan.
            if (typeof rootEl === 'undefined') {
                rootEl = document;
            }

            // Resolve the tier's state-class tokens once via the contract
            // surface. Going through this.stateClass() (rather than the
            // BS_COLLAPSE_STATE_CLASSES constant directly) lets a future
            // tier that overrides only stateClass while inheriting this
            // createInstances pick up the override automatically.
            const rootClass = this.stateClass('root');
            const shownClass = this.stateClass('shown');
            const hiddenClass = this.stateClass('hidden');

            // Collect candidates: rootEl itself (if a matching element), then
            // descendants. nodeType === 1 guards against document / fragment
            // roots passed by callers.
            const candidates = [];
            if (rootEl.nodeType === 1 && rootEl.matches('[data-zit-collapsible]')) {
                candidates.push(rootEl);
            }
            rootEl.querySelectorAll('[data-zit-collapsible]').forEach((el) => candidates.push(el));

            // Apply state classes to each unprocessed element.
            candidates.forEach((el) => {
                // Skip elements already processed by a previous call.
                if (el.dataset.zitCollapseInitialized === 'true') {
                    return;
                }
                // Apply the root marker class so Bootstrap.Collapse treats this element as collapsible.
                // .split(' ') because stateClass may return whitespace-separated tokens (future tiers).
                el.classList.add(...rootClass.split(' '));
                // Apply the initial visibility state — default 'false' (expanded) when the attr is absent.
                const initiallyCollapsed = el.dataset.zitInitiallyCollapsed === 'true';
                const initialStateClass = initiallyCollapsed ? hiddenClass : shownClass;
                el.classList.add(...initialStateClass.split(' '));
                // Mark as processed so re-scans (boot then per-subtree) are no-ops on this element.
                el.dataset.zitCollapseInitialized = 'true';
            });
        },

        create: function (el, opts) {
            // Construct the underlying Bootstrap widget.
            const instance = new bootstrap.Collapse(el, opts);
            // Adapter exposing the contract surface. show/hide/dispose are
            // thin passthroughs; on() maps neutral event names to Bootstrap's.
            return {
                show: function () { instance.show(); },
                hide: function () { instance.hide(); },
                dispose: function () { instance.dispose(); },
                on: function (eventName, handler) {
                    // Translate the neutral name to Bootstrap's namespaced event.
                    const bsEventName = BS_COLLAPSE_EVENTS[eventName];
                    if (typeof bsEventName === 'undefined') {
                        // Fail loudly so callers don't silently miss events.
                        throw new Error('zit collapse.on: unknown event "' + eventName + '"');
                    }
                    // Bootstrap fires shown.bs.collapse / hidden.bs.collapse on the element itself.
                    el.addEventListener(bsEventName, handler);
                }
            };
        }
    },

    // Toast: one-shot show. getOrCreateInstance avoids leaking instances on
    // the same element if the caller invokes show() multiple times.
    toast: {
        show: function (el) {
            bootstrap.Toast.getOrCreateInstance(el).show();
        }
    },

    // Popover: wraps Bootstrap.Popover to expose neutral event names via
    // on() alongside the show/hide/dispose passthrough.
    popover: {
        create: function (el, opts) {
            // Construct the underlying Bootstrap widget.
            const instance = new bootstrap.Popover(el, opts);
            // Adapter exposing the contract surface. on() maps neutral event
            // names to Bootstrap's namespaced ones, fired on the trigger el.
            return {
                show: function () { instance.show(); },
                hide: function () { instance.hide(); },
                dispose: function () { instance.dispose(); },
                on: function (eventName, handler) {
                    // Translate the neutral name to Bootstrap's namespaced event.
                    const bsEventName = BS_POPOVER_EVENTS[eventName];
                    if (typeof bsEventName === 'undefined') {
                        // Fail loudly so callers don't silently miss events.
                        throw new Error('zit popover.on: unknown event "' + eventName + '"');
                    }
                    // Bootstrap fires shown.bs.popover / hidden.bs.popover on the trigger element.
                    el.addEventListener(bsEventName, handler);
                }
            };
        }
    },

    // Submenu: Bootstrap-flavoured dropdown menu wrapper.
    submenu: {
        // Bulk-init any zit-namespaced dropdown toggles on the page.
        // Bootstrap auto-inits markup that uses data-bs-toggle="dropdown",
        // but zit markup uses data-zit-submenu-toggle so we initialise
        // those explicitly. Idempotent: skips toggles already wired.
        createInstances: function () {
            // Locate every zit submenu toggle in the document.
            const toggles = document.querySelectorAll('[data-zit-submenu-toggle]');
            // Iterate over the static NodeList so we can safely create instances.
            toggles.forEach((toggleEl) => {
                // Skip toggles that already have a Bootstrap.Dropdown attached.
                const existing = bootstrap.Dropdown.getInstance(toggleEl);
                if (existing === null) {
                    // Construct a new Dropdown instance bound to this toggle.
                    new bootstrap.Dropdown(toggleEl);
                }
            });
        },

        // Retrieve a submenu instance attached to el. Caller may pass either
        // the dropdown toggle (Bootstrap-shaped) or the menu element (base
        // call sites historically passed the menu); we normalise both.
        get: function (el) {
            // Try el itself as the toggle first (the canonical Bootstrap entry point).
            let instance = bootstrap.Dropdown.getInstance(el);

            // If el wasn't a toggle, look for a toggle inside or alongside it.
            if (instance === null) {
                // Search descendants for any zit/bs submenu toggle.
                const toggle = el.querySelector('[data-zit-submenu-toggle], [data-bs-toggle="dropdown"]');
                if (toggle !== null) {
                    // Reuse an existing instance, or create on demand.
                    instance = bootstrap.Dropdown.getInstance(toggle) || new bootstrap.Dropdown(toggle);
                }
            }

            // No toggle found anywhere — caller asked about a non-submenu element.
            if (instance === null || typeof instance === 'undefined') {
                return null;
            }

            // Bootstrap fires shown.bs.dropdown / hidden.bs.dropdown on the toggle's
            // parent (.dropdown wrapper). Capture it once for event subscription.
            const eventTarget = instance._element.closest('.dropdown') || instance._element.parentElement;

            // Return an adapter that exposes the contract surface (on / show).
            return {
                // Subscribe to lifecycle events using neutral names.
                on: function (eventName, handler) {
                    // Map the neutral name to Bootstrap's namespaced event.
                    const bsEventName = BS_DROPDOWN_EVENTS[eventName];
                    if (typeof bsEventName === 'undefined') {
                        // Unknown event name — fail loudly so callers don't silently miss events.
                        throw new Error('zit submenu.on: unknown event "' + eventName + '"');
                    }
                    // Wire the handler on the dropdown root.
                    eventTarget.addEventListener(bsEventName, handler);
                },
                // Open the submenu. Bootstrap anchors against its own toggle
                // regardless of targetEl (KTMenu's "anchor to arbitrary element"
                // semantic is not native to bootstrap.Dropdown — see file header).
                show: function (/* targetEl */) {
                    instance.show();
                }
            };
        }
    },

    // Drawer: bootstrap.Offcanvas wrapper. Always overlay-mode — Bootstrap
    // has no inline-aside layout primitive. A pure-Bootstrap leaf using
    // bootstrap5 directly renders this drawer as a sliding overlay at
    // every viewport.
    drawer: {
        // Retrieve (or create) an Offcanvas instance attached to el.
        get: function (el) {
            // Offcanvas requires the .offcanvas class on the element to position correctly;
            // we rely on the consumer to set up the markup. getOrCreateInstance is forgiving.
            const instance = bootstrap.Offcanvas.getOrCreateInstance(el);

            // Adapter mapping contract names to Bootstrap method names.
            return {
                // Slide the drawer in (overlay mode).
                show: function () { instance.show(); },
                // Slide the drawer out.
                hide: function () { instance.hide(); },
                // Toggle visibility.
                toggle: function () { instance.toggle(); },
                // Release listeners and remove residual state.
                destroy: function () { instance.dispose(); }
            };
        }
    },

    // BlockUI: CSS-only overlay. Adds a positioned overlay div to the host
    // element while blocked; removes it on release. No third-party JS.
    blockUI: {
        create: function (el) {
            // The overlay element is created lazily on the first block() call
            // and torn down on release()/destroy().
            let overlay = null;

            // Helper to mount the overlay — adds the host class so we can
            // pin position:relative on the host without overwriting any
            // existing inline style.
            const mount = function () {
                // No-op if an overlay is already mounted (calling block() twice in a row).
                if (overlay !== null) {
                    return;
                }
                // Mark the host so the CSS rule kicks in (position:relative).
                el.classList.add('zit-blockui-host');
                // Build the overlay node (Bootstrap spinner inside).
                overlay = document.createElement('div');
                overlay.className = 'zit-blockui-overlay';
                overlay.innerHTML = '<div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div>';
                // Attach to the host so it inherits the host's position:relative box.
                el.appendChild(overlay);
            };

            // Helper to unmount the overlay — symmetric counterpart to mount().
            const unmount = function () {
                // No-op if there's no overlay to remove.
                if (overlay === null) {
                    return;
                }
                // Remove the overlay node from the DOM.
                overlay.remove();
                // Drop the marker class so the host returns to its previous styling.
                el.classList.remove('zit-blockui-host');
                // Clear the cached reference so a future block() can re-mount.
                overlay = null;
            };

            return {
                // Enter the blocked state.
                block: function () { mount(); },
                // Leave the blocked state but keep the instance reusable.
                release: function () { unmount(); },
                // Tear down completely (release + drop any residual state).
                destroy: function () { unmount(); }
            };
        }
    },

    // Breakpoint: matchMedia against Bootstrap's px breakpoints.
    breakpoint: {
        isUp: function (name) {
            // Look up the px value for the requested breakpoint.
            const minPx = BS_BREAKPOINTS[name];
            // Fail loudly on typos — silently returning false would mask bugs.
            if (typeof minPx === 'undefined') {
                throw new Error('zit breakpoint.isUp: unknown breakpoint name "' + name + '"');
            }
            // Run the media query and return the boolean match state.
            return window.matchMedia('(min-width: ' + minPx + 'px)').matches;
        }
    },

    // Activation state — Bootstrap and Metronic both use the bare class
    // names 'active' / 'disabled'. Wrapping the lookup behind a hook keeps
    // call sites (pagination, selectSearchable) free of literal tokens
    // and lets a future tier remap them without touching consumers.
    activation: {
        stateClass: function (name) {
            const tokenString = BS_ACTIVATION_STATE_CLASSES[name];
            if (typeof tokenString === 'undefined') {
                throw new Error('zit activation.stateClass: unknown state "' + name + '"');
            }
            return tokenString;
        }
    },

    // Stubs for hooks not yet implemented in bootstrap5.
    // Step 5d will add image-input / password-meter implementations
    // (Bootstrap-flavoured fallback in this file, KT-wrapping in metronic_8).
    imageInput: {
        create: function () {
            throw new Error(
                'zit behaviour not implemented in bootstrap5: imageInput.create' +
                ' — extend with metronic_8 (or another tier) that provides it'
            );
        }
    },
    passwordMeter: {
        create: function () {
            throw new Error(
                'zit behaviour not implemented in bootstrap5: passwordMeter.create' +
                ' — extend with metronic_8 (or another tier) that provides it'
            );
        }
    },

    // Bridge zit-namespaced markup onto data-bs-* attributes Bootstrap's
    // auto-discovery looks for. Walks rootEl's descendants and mirrors:
    //   - [data-zit-toggle="collapse"]   -> data-bs-toggle, data-bs-target
    //     (paired with data-zit-collapse-target on the same element)
    //   - [data-zit-toggle="tooltip"]    -> data-bs-toggle, data-bs-title,
    //     data-bs-dismiss (the latter for the m8-specific 'click' auto-hide
    //     extension to bootstrap.Tooltip; harmless on pure zb5)
    //   - [data-zit-dismiss="toast"]     -> data-bs-dismiss
    // Idempotent — helper.mirrorDataAttribute only writes when the
    // destination key is unset, so explicit overrides on the destination
    // (and re-running the bridge over the same subtree) are preserved.
    bridgeTierAttrs: function (rootEl) {
        // Collapse triggers carry data-zit-toggle="collapse" + the target ref.
        // We use data-zit-collapse-target (not data-zit-target) because
        // data-zit-target is already taken by base's link/anchor target
        // attribute (action.tpl, browseTree.tpl); the bridge rewrites it to
        // Bootstrap's generic data-bs-target on the way out.
        rootEl.querySelectorAll('[data-zit-toggle="collapse"]').forEach((el) => {
            helper.mirrorDataAttribute(el, 'toggle', 'bs');
            helper.mirrorDataAttribute(el, 'collapseTarget', 'bs', 'zit', 'target');
        });
        // Tooltip triggers — mirror toggle, title, and the optional dismiss
        // marker (m8 piggybacks on bootstrap's data-bs-dismiss="click" for
        // tooltip auto-hide; non-m8 leaves dismiss absent and that's fine).
        rootEl.querySelectorAll('[data-zit-toggle="tooltip"]').forEach((el) => {
            helper.mirrorDataAttribute(el, 'toggle', 'bs');
            helper.mirrorDataAttribute(el, 'title', 'bs');
            helper.mirrorDataAttribute(el, 'dismiss', 'bs');
        });
        // Toast close buttons — Bootstrap's Toast widget reads data-bs-dismiss="toast"
        // to wire its built-in close behaviour.
        rootEl.querySelectorAll('[data-zit-dismiss="toast"]').forEach((el) => {
            helper.mirrorDataAttribute(el, 'dismiss', 'bs');
        });
    }
};

module.exports = behaviors;
